import { Result, Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiClient } from '@/api/client';
import { getRuntimeConfig, navigableUrl } from '@/config/runtimeConfig';
import { releaseAllLocksForSignOff } from '@/originator/editorLock';

import { clearDevIdentity, getDevRole, isDevLoginEnabled } from './devLogin';
import { invalidateSession, useSession } from './useSession';

/**
 * Per-environment landing page for signed-out staff, from config.json.
 *
 * Returns null when unset (localhost, and any environment BMO has not given us a URL
 * for yet) or when the configured value is not an absolute http(s) url — a typo or a
 * `javascript:` value in a deploy-time config must not become a redirect. Either way
 * the caller falls back to the in-app confirmation page.
 */
function postLogoutRedirectUrl(): string | null {
  try {
    return navigableUrl(getRuntimeConfig().auth?.postLogoutRedirectUrl);
  } catch {
    // Runtime config not loaded (e.g. unit tests) → no redirect.
    return null;
  }
}

/**
 * Logs the user out. In Dev Login mode clears the picked identity and returns to the
 * role picker. For all BFF sessions (Entra + ISAM) POSTs to /auth/logout, clears the
 * server session + cookie, then sends the user on to their post-logout destination.
 */
export function LogoutPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { user, loading } = useSession();
  const startedRef = useRef(false);

  useEffect(() => {
    // Dev Login never redirects anywhere external, so it need not wait on /me. Every
    // other session does: the destination below depends on WHICH IdP signed this user
    // in, and /me is the only thing that says. Reaching /auth/logout by a direct
    // navigation (rather than from the header, where the session is long since cached)
    // is the case that would otherwise resolve the role as null and guess wrong.
    if (!isDevLoginEnabled() && loading) return;
    if (startedRef.current) return;
    startedRef.current = true;

    // Hand back any editor locks FIRST, while the session cookie is still valid. After
    // invalidateSession()/POST /auth/logout the credentials are gone and the release would
    // 401, stranding every workflow this user had open until its TTL expires.
    //
    // Under Dev Login the role comes from the PICKED IDENTITY, not from /me — there is no
    // /me to ask, so reading `user` there resolves every synthetic identity to "not an
    // advisor" and hands Eve Advisor the staff landing page the check below exists to
    // keep her off.
    const isAdvisor = isDevLoginEnabled()
      ? getDevRole() === 'Advisor'
      : (user?.roles.includes('Advisor') ?? false);
    void releaseAllLocksForSignOff().finally(() => {
      runLogout(isAdvisor);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /**
   * Where a signed-out user lands, by one rule for every mode.
   *
   * Dev Login used to short-circuit this entirely and go straight to the picker, which
   * made the configured redirect unreachable on the one environment where it could be
   * exercised before BMO hands us the real URLs — the plumbing shipped untested.
   *
   * Advisors are still excluded: the configured URL is a staff landing page, and where
   * an ISAM session should land is an open question. `fallbackRoute` is the only thing
   * that differs between modes, because the dev picker is the useful dead-end there and
   * the confirmation page is the useful one everywhere else.
   */
  function finishLogout(isAdvisor: boolean, fallbackRoute: string) {
    const redirect = isAdvisor ? null : postLogoutRedirectUrl();
    if (redirect) {
      // replace(), not assign(): Back out of the destination must not return to
      // /auth/logout and re-run a sign-out against a session that is already gone.
      window.location.replace(redirect);
      return;
    }
    navigate(fallbackRoute, { replace: true });
  }

  function runLogout(isAdvisor: boolean) {
    if (isDevLoginEnabled()) {
      clearDevIdentity();
      // Same invalidation the real logout path below does. Without it the
      // outgoing identity's /me stays cached for the life of the document, so
      // signing back in as someone else keeps answering as the previous user
      // (advisor-profile prefill being the visible symptom).
      invalidateSession();
      // No POST: a dev identity is sessionStorage and nothing else, so there is no
      // BFF session for the server to delete. The redirect still applies — when it
      // is unconfigured, the identity picker is where a tester wants to be anyway.
      finishLogout(isAdvisor, '/auth/dev-login');
      return;
    }

    invalidateSession();
    apiClient
      .post<{ logoutUrl: string }>('/auth/logout', null, {
        baseURL: '/',
      })
      .then(() => finishLogout(isAdvisor, '/auth/logged-out'))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Logout failed.');
      });
  }

  if (error) {
    return <Result status="error" title="Sign-out failed" subTitle={error} />;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
      <Spin size="large" />
    </div>
  );
}
